import { useCallback, useMemo, useState } from 'react';
import { fileTypeFromBuffer } from 'file-type';
import { isEmpty, isUndefined } from 'lodash';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import type { CompanyDocuments, DelegateDocumentRequest, DocumentReferencesView, FileView, MeasurementDate, RedelegateDocumentRequest } from 'api';
import { ApiService, DocumentsService } from 'api';
import { globalAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { EXTERNAL_VALUATION_REFERENCE_TITLE } from 'common/constants/documents';
import * as messages from 'common/constants/messages/validations';
import { OTHER } from 'common/constants/process-management';
import { useStore } from 'common/store';
import validFileTypes from 'components/Documents/validFileTypes';
import FilesService from 'services/documents';
import { Logger } from 'utilities';
import { useResponse } from './useResponse';
import validMimeTypeMN from '../../components/Documents/validMIMETypes';

// Import existing API model types

// Essential type definitions only
interface ExtendedMeasurementDate extends MeasurementDate {
  cmd_id?: string | number;
}

interface FileData {
  file: FileView;
  firmId?: string | number;
  md_documents?: string | number;
}

interface DownloadDocumentOptions {
  showErrorNotification?: boolean;
}

interface DownloadDocumentContentOptions extends DownloadDocumentOptions {
  fallbackToSignedUrl?: boolean;
}

export interface FileToSave {
  document_reference_id: string | number;
  filename: string;
  file_type: string;
}

export const useDocuments = (selectedMeasurementDate?: ExtendedMeasurementDate) => {
  const filesService = useMemo(() => new FilesService(), []);
  const [companyDocuments, setCompanyDocuments] = useState<CompanyDocuments[] | undefined>();
  const [currentDocuments, setCurrentDocuments] = useState<CompanyDocuments | undefined>();
  const [featuresObjects, setFeaturesObjects] = useState<any>();
  const [filesToSave, setFilesToSave] = useState<FileToSave[]>([]);
  const [progress, setProgress] = useState(0);
  const [isValidType, setIsValidType] = useState<boolean>();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [{ companyInfo }] = useStore();
  const { errorNotification, successNotification, processErrorResponse } = useResponse();
  const { id: company_id } = companyInfo as any;
  const [{ user }, dispatch] = useStore();
  const { requestId: requestIdFromParams, hmacToken: hmacTokenFromParams } = useParams<{
    requestId?: string;
    hmacToken?: string;
  }>();
  const { currentValuationScalarOpinion } = useSelector((state: any) => state.valuation);
  const reportId = currentValuationScalarOpinion?.report_date?.id;

  const getPortalLinks = useCallback(async () => {
    if (selectedMeasurementDate) {
      return ApiService.apiTasksPortalLinksRead(selectedMeasurementDate.cmd_id ?? reportId);
    }
  }, [selectedMeasurementDate, reportId]);

  const getPreSignedUrl = async (selectedMD: string | number, fileExtension: string, autoClassify: boolean = false) => {
    try {
      return await filesService.getPreSignedUrl(String(selectedMD), fileExtension, autoClassify);
    } catch (error) {
      Logger.error('Error getting pre-signed URL', error);
      return error;
    }
  };

  const createFileRepresentation = async (id: string | number, data: any, fileName: string) => {
    try {
      const response = await filesService.createFileRepresentation(String(id), data);
      successNotification(messages.DOCUMENT_UPLOAD_SUCCESS(fileName));
      return response;
    } catch (error) {
      Logger.error(`Error creating file representation for ${fileName}`, error);
      errorNotification(messages.DOCUMENT_UPLOAD_ERROR(fileName));
      return error;
    }
  };

  const getDocuments = async (mdId: string | number, companyId?: string | number) => {
    try {
      setCompanyDocuments(undefined);
      setIsLoading(true);
      const response = (await filesService.getCompanyDocuments(String(company_id || companyId))) as any;

      if (response.company_documents) {
        const foundCurrentDocuments = response.company_documents?.find((d: any) => d.measurement_date.id === mdId);
        setCurrentDocuments(foundCurrentDocuments);
      }
      const measurementDateWithFiles =
        response.company_documents?.filter((md: any) => md.files && md.files.length > 0) || [];
      if (isEmpty(measurementDateWithFiles)) {
        setCompanyDocuments([]);
      } else {
        setCompanyDocuments(response.company_documents);
      }
      setIsLoading(false);
      return response.company_documents;
    } catch (error) {
      Logger.error('Error getting company documents', error);
      setCompanyDocuments([]);
    }
  };

  const renameFileIfExists = (fileName: string, existingFiles: FileData[]) => {
    const fileExtension = fileName.split('.').pop();
    const baseName = fileName.replace(`.${fileExtension}`, '');
    let newFileName = baseName;
    let counter = 1;

    const existingFileNames = existingFiles.map(fileObj => `${fileObj.file.filename.toLowerCase()}`);

    while (existingFileNames.includes(newFileName.toLowerCase())) {
      newFileName = `${baseName} (${counter})`;
      counter += 1;
    }

    return newFileName;
  };

  const uploadFile = async (url: string, file: File, existingFiles: FileData[] = []) => {
    const fileName = renameFileIfExists(file.name, existingFiles);
    const renamedFile = new File([file], fileName, { type: file.type });

    const otherParams = {
      method: 'PUT',
      body: renamedFile,
    };

    try {
      const response = await fetch(url, otherParams).then(res => res);
      return { status: response.status, filename: fileName };
    } catch (error) {
      Logger.error(`Error uploading file ${fileName} to ${url}`, error);
      errorNotification(messages.DOCUMENT_UPLOAD_ERROR(file.name));
      return error;
    }
  };

  const deleteFile = async (documentsId: string | number, file: any) => {
    try {
      const response = await filesService.deleteDocument(String(documentsId), String(file.id));
      successNotification(messages.DOCUMENT_DELETE_SUCCESS(`${file.filename}.${file.file_type}`));
      return response;
    } catch (error) {
      errorNotification(messages.DOCUMENT_DELETE_ERROR(`${file.filename}.${file.file_type}`));
      return error;
    }
  };

  const updateFile = async (documentsId: string | number, fileId: string | number, data: any, showMsg?: boolean) => {
    try {
      const response = await filesService.updateDocument(String(documentsId), String(fileId), data);
      if (showMsg) {
        successNotification(messages.DOCUMENT_UPDATED_SUCCESS(`${data.filename}`));
      }
      return response;
    } catch (error) {
      if (showMsg) {
        errorNotification(messages.DOCUMENT_UPDATED_ERROR(`${data.filename}`));
      }
      return error;
    }
  };

  const downloadDocument = async (
    fileData: FileData,
    openInNewTab: boolean = true,
    options: DownloadDocumentOptions = {}
  ): Promise<{ signed_url: string }> => {
    const { showErrorNotification = true } = options;
    const FILE_NAME = `${fileData.file.filename}.${fileData.file.file_type}`;
    try {
      const response = await ApiService.apiDocumentsDownloadRead(
        String(fileData.file.id),
        undefined,
        String(fileData.firmId || '')
      );
      if (response?.signed_url && openInNewTab) {
        globalThis.location.href = response.signed_url;
        successNotification(messages.DOCUMENT_DOWNLOAD_SUCCESS(FILE_NAME));
      }
      return response as { signed_url: string };
    } catch (error) {
      Logger.error(`Error downloading document ${FILE_NAME}`, error);
      if (showErrorNotification) {
        errorNotification(messages.DOCUMENT_DOWNLOAD_ERROR(FILE_NAME));
      }
      return { signed_url: '' };
    }
  };

  const downloadDocumentContent = async (
    fileData: FileData,
    options: DownloadDocumentContentOptions = {}
  ): Promise<any> => {
    const { fallbackToSignedUrl = false, showErrorNotification = true } = options;
    const FILE_NAME = `${fileData.file.filename}.${fileData.file.file_type}`;
    const getSignedUrlFallback = async (response?: any) => {
      if (!fallbackToSignedUrl) {
        return response ?? { content: null, content_type: null, filename: null };
      }

      const downloadResponse = await downloadDocument(fileData, false, { showErrorNotification: false });
      if (!downloadResponse.signed_url) {
        return response ?? { content: null, content_type: null, filename: null };
      }

      return {
        ...response,
        blob_url: downloadResponse.signed_url,
        signed_url: downloadResponse.signed_url,
      };
    };

    try {
      const response = (await filesService.downloadDocumentContent(
        String(fileData.file.id),
        String(fileData.firmId || '')
      )) as any;

      // Handle base64 response format from new endpoint
      if (response?.content && response?.content_type && response?.filename) {
        // Create blob URL from base64 content for display
        const binaryString = atob(response.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: response.content_type });
        const blobUrl = URL.createObjectURL(blob);

        return {
          ...response,
          blob_url: blobUrl,
          blob,
        };
      }

      if (response?.signed_url && !response?.blob_url) {
        return {
          ...response,
          blob_url: response.signed_url,
        };
      }

      if (response?.blob_url) {
        return response;
      }

      return getSignedUrlFallback(response);
    } catch (error) {
      Logger.error(`Error downloading document ${FILE_NAME}`, error);
      if (showErrorNotification) {
        errorNotification(messages.DOCUMENT_DOWNLOAD_ERROR(FILE_NAME));
      }
      return getSignedUrlFallback();
    }
  };

  const downloadValuationReport = async (path: string) => {
    const FILE_NAME = path.split('/').pop() || '';
    try {
      const response = (await filesService.downloadValuationReport(
        selectedMeasurementDate?.cmd_id ? Number(selectedMeasurementDate.cmd_id) : 0
      )) as any;
      if (response?.signed_url) {
        window.open(response.signed_url, '_blank');
      }
      successNotification(messages(FILE_NAME));
      return true;
    } catch (error) {
      Logger.error(`Error downloading valuation report ${FILE_NAME}`, error);
      errorNotification(messages.DOCUMENT_DOWNLOAD_ERROR(FILE_NAME));
      return false;
    }
  };

  const deleteMultipleFiles = async (filesData: FileData[]) => {
    try {
      await Promise.all(
        filesData.map(async fileData => {
          await filesService.deleteDocument(String(fileData.md_documents), String(fileData.file.id));
        })
      );
      successNotification(messages.DOCUMENT_DELETE_SUCCESS('multiple files'));
    } catch (error) {
      Logger.error('Error deleting multiple files', error);
      errorNotification(messages.DOCUMENT_DELETE_ERROR('multiple files'));
    }
  };

  const downloadMultipleDocuments = async (filesData: FileData[], firmId: string | number) => {
    try {
      const results = await Promise.all(
        filesData.map(async fileData => {
          const response = (await filesService.downloadDocument(String(fileData.file.id), String(firmId))) as any;
          const url = response?.signed_url;
          const filename = `${fileData.file.filename}.${fileData.file.file_type}`;
          return url ? { url, filename } : null;
        })
      );
      await Promise.all(
        results
          .filter((item): item is NonNullable<typeof item> => item !== null)
          .map(async item => {
            try {
              const res = await fetch(item.url);
              if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
              }
              const blob = await res.blob();
              const objectUrl = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = objectUrl;
              link.download = item.filename;
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              link.remove();
              URL.revokeObjectURL(objectUrl);
            } catch (fetchError) {
              Logger.error(`Error downloading ${item.filename}`, fetchError);
            }
          })
      );
      successNotification(messages.DOCUMENT_DOWNLOAD_SUCCESS('multiple files'));
    } catch (error) {
      Logger.error('Error downloading multiple documents', error);
      errorNotification(messages.DOCUMENT_DOWNLOAD_ERROR('multiple files'));
    }
  };

  const getReferencesByMeasurementDateId = async (measurementDateId: string | number) => {
    try {
      return await filesService.getReferencesByMeasurementDateId(String(measurementDateId));
    } catch (error) {
      return error;
    }
  };

  const getFeaturesObjectsById = useCallback(
    async (mdId: string | number) => {
      try {
        setIsLoading(true);
        const response = await filesService.getFeaturesObjects(String(mdId));
        if (response) {
          setFeaturesObjects(response);
        }
        setIsLoading(false);
      } catch (error) {
        Logger.error('Error getting features objects by measurement date id', error);
        setFeaturesObjects(null);
      }
    },
    [filesService]
  );

  const getFeaturesObjects = async () => {
    if (selectedMeasurementDate?.id) {
      return getFeaturesObjectsById(selectedMeasurementDate.id);
    }
  };

  const addDocumentReference = async (mdId: string | number, fileId: string | number, data: any) => {
    try {
      const response = await filesService.addDocumentReference(String(mdId), String(fileId), data);
      successNotification(messages.MOVE_FILE_SUCCESS);
      return response;
    } catch (error: any) {
      const fileAlreadyExists = error.response?.text?.includes('already exists');
      errorNotification(fileAlreadyExists ? messages.FILE_ALREADY_EXISTS : messages.MOVE_FILE_ERROR);
      return error;
    }
  };

  const addDocumentReferencesObject = useCallback(
    async (mdId: string | number, data: any, showMsg?: boolean) => {
      try {
        const response = await ApiService.apiDocumentsDocumentReferenceObjectsCreate(String(mdId), data);
        if (showMsg) {
          successNotification(messages.DOCUMENT_UPDATED_SUCCESS(`${data.filename}`));
        }
        return response;
      } catch (error) {
        if (showMsg) {
          errorNotification(messages.DOCUMENT_UPDATED_ERROR(`${data.filename}`));
        }
        return error;
      }
    },
    [errorNotification, successNotification]
  );

  const foundReferencedDocuments = (docs: any[] | null, currentPage: string, referencedFeatureId: string | number) => {
    if (!docs) {
      return [];
    }

    const documentsWithReference = docs.filter(document => document.referenced_in?.length > 0);
    const referencedDocuments = documentsWithReference.flatMap(document => document.referenced_in);
    let referencedInCurrentPage: any[] = [];

    if (currentPage === EXTERNAL_VALUATION_REFERENCE_TITLE) {
      referencedInCurrentPage = referencedDocuments.filter(
        document => document.reference_object_id === referencedFeatureId
      );
    } else {
      referencedInCurrentPage = referencedDocuments.filter(document => document.reference_object_type === currentPage);
    }
    const referencedIds = referencedInCurrentPage.map(data => data.document_reference);

    return docs.filter(document => referencedIds.includes(document.document_reference_id));
  };

  const getReferenceCreateList = (selectedValues: any[], referencedFeatureId: string | number, referenceType: string) =>
    selectedValues.map(value => ({
      reference_object_id: referencedFeatureId,
      reference_object_type: referenceType,
      document_reference_id: value.id || value.document_reference_id,
    }));

  const getReferencesData = useCallback(
    (selectedValues: any[], referencedFeatureId: string | number, referenceType: string) => ({
      create_list: getReferenceCreateList(selectedValues, referencedFeatureId, referenceType),
      delete_list: [],
    }),
    []
  );

  const addReferenceForExistingDocuments = useCallback(
    async (
      selectedMeasurementDateParam: string | number,
      selectedValues: FileToSave[],
      referencedFeatureId: string | number,
      referenceType: string
    ) => {
      try {
        const updatedReferenceData = getReferencesData(selectedValues, referencedFeatureId, referenceType);
        const requestData = {
          ...updatedReferenceData,
          measurement_date_id: selectedMeasurementDateParam,
        };
        await addDocumentReferencesObject(selectedMeasurementDateParam, requestData);
        setFilesToSave([]);
        successNotification(messages.REFERENCE_ADDED_SUCCESS);
      } catch (error) {
        Logger.error('Error adding reference for existing documents', error);
        errorNotification(messages.REFERENCE_ADDED_ERROR);
      }
    },
    [addDocumentReferencesObject, errorNotification, getReferencesData, successNotification]
  );

  const createSubFolder = async (docsId: string | number, data: { name: string }) => {
    try {
      const response = await filesService.createSubFolder(String(docsId), data);
      successNotification(messages.FOLDER_CREATED_SUCCESS(`${data.name}`));
      return response;
    } catch (error) {
      errorNotification(messages.FOLDER_CREATED_ERROR(`${data.name}`));
      return error;
    }
  };

  const updateSubFolder = async (docsId: string | number, folderId: string | number, data: { name: string }) => {
    try {
      const response = await filesService.updateSubFolder(String(docsId), String(folderId), data);
      successNotification(messages.FOLDER_UPDATED_SUCCESS(`${data.name}`));
      return response;
    } catch (error) {
      errorNotification(messages.FOLDER_UPDATED_ERROR(`${data.name}`));
      return error;
    }
  };

  const deleteFolder = async (docsId: string | number, folderId: string | number, folderName: string) => {
    try {
      const response = await filesService.deleteFolder(String(docsId), String(folderId), folderName);
      successNotification(messages.FOLDER_DELETED_SUCCESS(folderName));
      return response;
    } catch (error) {
      errorNotification(messages.FOLDER_DELETE_ERROR(folderName));
      return error;
    }
  };

  const addFilesToFolder = async (documentsId: string | number, data: any, showMsg?: boolean) => {
    try {
      const response = await filesService.addFilesToFolder(String(documentsId), data);
      if (showMsg) {
        response.forEach((fileRef: any) => {
          successNotification(messages.DOCUMENT_UPDATED_SUCCESS(`${fileRef.file.filename}`));
        });
      }
      return response;
    } catch (error) {
      if (showMsg) {
        errorNotification(messages.DOCUMENT_UPDATED_ERROR(`${data.filename}`));
      }
      return error;
    }
  };

  const uploadAndCreateRepresentation = async (fileProps: any, fileExtension: string) => {
    dispatch(globalAction.showLoadingProgress(true));
    const {
      file,
      fromReferenceWidget,
      mdId,
      currentDocumentsId,
      setCompanyDocumentsParam,
      setIsLoadingParam,
      setIsFileUploaded,
      setFilesToSaveParam,
      files,
      autoClassify,
    } = fileProps;

    try {
      setIsValidType(true);
      console.log('[DEBUG] calling getPreSignedUrl with autoClassify:', autoClassify);
      const preSignedUrlResult = await getPreSignedUrl(mdId, fileExtension, autoClassify);

      if (preSignedUrlResult instanceof Error) {
        setIsValidType(false);
        return;
      }

      const preSignedUrl = preSignedUrlResult as any;
      console.log('[DEBUG] preSignedUrl response:', preSignedUrl);
      setProgress(25);

      const uploadResult = await uploadFile(preSignedUrl.url, file, files);

      if (uploadResult instanceof Error) {
        setIsValidType(false);
        return;
      }

      const { status, filename } = uploadResult as any;
      setProgress(50);

      if (status === 200) {
        setProgress(85);
        const extractedFileType = preSignedUrl.key.split('.').pop();
        const takePathBeforeLastSlash = preSignedUrl.key.split('/').slice(0, -1).join('/');
        const extractedPath = preSignedUrl.key.split('/').pop();
        const extractKeyWithoutType = extractedPath.split('.').slice(0, -1).join('.');

        const data = {
          guid: extractKeyWithoutType,
          folder_id: null,
          filename,
          file_path: takePathBeforeLastSlash,
          file_type: extractedFileType,
          md_documents_id: currentDocumentsId,
          uploaded_by: (user as any).id,
          auto_classify: preSignedUrl.auto_classify ?? false,
        };

        const createResult = await createFileRepresentation(currentDocumentsId, data, filename);

        if (createResult instanceof Error) {
          setIsValidType(false);
          return;
        }

        const res = createResult as any;

        const fileToSave = {
          document_reference_id: res.document_reference_id,
          filename,
          file_type: data.file_type,
        };

        if (res.file === 'File created successfully') {
          setProgress(95);
          setIsLoadingParam(true);
          if (setCompanyDocumentsParam) {
            setCompanyDocumentsParam(await getDocuments(company_id));
          }
          setProgress(100);
          setIsLoadingParam(false);
          dispatch(globalAction.showLoadingProgress(false));
        } else {
          setIsValidType(false);
        }

        if (fromReferenceWidget && setFilesToSaveParam && setIsFileUploaded) {
          setFilesToSaveParam((prevState: FileToSave[]) => [...prevState, fileToSave]);
          setIsFileUploaded(true);
        }

        return createResult;
      } 
      setIsValidType(false);
      
    } catch (error) {
      setIsValidType(false);
      dispatch(globalAction.showLoadingProgress(false));
    }
  };

  const validateAndUploadFile = async (fileProps: any) => {
    const { file } = fileProps;

    const fileExtension = file.name?.split('.').pop()?.toLowerCase();
    const buffer = await file.arrayBuffer();
    const mimeMN = await fileTypeFromBuffer(buffer).then((res: any) => {
      if (['csv', 'txt'].includes(fileExtension!) && isUndefined(res)) {
        return fileExtension;
      }

      if (fileExtension === 'md') {
        return 'text/markdown';
      }

      if (res?.mime) {
        return res?.mime;
      }
      return null;
    });

    try {
      if (validFileTypes.includes(fileExtension!) && validMimeTypeMN.includes(mimeMN)) {
        setProgress(10);
        const results = await uploadAndCreateRepresentation(fileProps, fileExtension!);
        return results;
      } 
      setIsValidType(false);
      
    } catch (error) {
      setIsValidType(false);
      throw new Error(String(error));
    }
  };

  const sendDocumentRequest = async (data: any) => {
    try {
      dispatch(globalAction.showLoadingProgress(true));
      const response = await filesService.sendDocumentRequest(data);
      successNotification(messages.DOCUMENT_REQUEST_SENT_SUCCESS);
      dispatch(globalAction.showLoadingProgress(false));
      return response;
    } catch (error) {
      errorNotification(messages.DOCUMENT_REQUEST_SENT_ERROR);
      dispatch(globalAction.showLoadingProgress(false));
      return error;
    }
  };

  const validateDocumentRequestToken = async (requestId: string, token: string) => {
    try {
      return await filesService.validateDocumentRequestToken(requestId, token);
    } catch (error) {
      return error;
    }
  };

  const generatePostSignedUrlViaHMACToken = async (
    requestId: string,
    token: string,
    mdId: string | number,
    type: string
  ) => {
    try {
      return await filesService.generatePostSignedUrlViaHMACToken(requestId, token, String(mdId), type);
    } catch (error) {
      return error;
    }
  };

  const updateRequestedFileUploadStatus = async (requestId: string, token: string, data: any) => {
    try {
      return await filesService.updateRequestedFileUploadStatus(requestId, token, data);
    } catch (error) {
      return error;
    }
  };

  const updateTaskStatusFromPortal = async (requestId: string, token: string, taskId: string | number, data: any) => {
    try {
      return await filesService.updateTaskStatusFromPortal(requestId, token, String(taskId), data);
    } catch (error) {
      return error;
    }
  };

  const uploadRequestedFiles = async (uploadedFilesProps: any) => {
    const {
      droppedFile,
      requestedFilesIds,
      task,
      setUploadedFiles,
      hmacToken,
      requestId,
      files,
      showNotifications = true,
    } = uploadedFilesProps;
    dispatch(globalAction.showLoadingProgress(true));
    const taskData = task.task || task;
    const pureRequestedFilesId = requestedFilesIds.filter((id: string | number) => id !== OTHER);
    const createOther = requestedFilesIds.length !== pureRequestedFilesId.length;
    const requestIdToUse = requestId || requestIdFromParams;
    const hmacTokenToUse = hmacToken || hmacTokenFromParams;

    try {
      setIsUploading(true);
      setProgress(10);
      const fileType = droppedFile?.name.split('.').pop()?.toLowerCase();
      const response = (await generatePostSignedUrlViaHMACToken(
        requestIdToUse!,
        hmacTokenToUse!,
        taskData.measurement_date.id,
        fileType!
      )) as any;
      setProgress(40);
      if (response?.key) {
        const uploadResult = await uploadFile(response.url, droppedFile, files);

        if (!(uploadResult instanceof Error)) {
          const { status, filename } = uploadResult as any;
          setProgress(80);
          if (status === 200) {
            const guid = response.key.split('/').pop()?.split('.').shift();
            const file_path = response.key.replace(`/${guid}.${fileType}`, '');
            const postData = {
              md_documents_id: taskData.measurement_date_documents_id,
              guid: guid!,
              filename,
              file_type: fileType!,
              file_path,
              requested_files: pureRequestedFilesId,
              create_other: createOther,
            };
            const updatedFile = (await updateRequestedFileUploadStatus(
              requestIdToUse,
              hmacTokenToUse,
              postData
            )) as any;
            if (updatedFile?.requested_file?.length > 0) {
              setProgress(100);
              setUploadedFiles((prevState: File[]) => [...prevState, droppedFile]);
              if (showNotifications) {
                successNotification(messages.DOCUMENT_UPLOAD_SUCCESS(droppedFile.name));
              }
            } else if (showNotifications) {
              errorNotification(messages.DOCUMENT_UPLOAD_ERROR(droppedFile.name));
            }
          }
        }
      }
    } catch (error) {
      Logger.error('Error uploading requested files', error);
      if (showNotifications) {
        errorNotification(messages.DOCUMENT_UPLOAD_ERROR(droppedFile.name));
      }
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
      setIsUploading(false);
    }
  };

  const hasDocuments = async (companyId: string | number, measurementDateId: string | number) => {
    dispatch(globalAction.showLoadingProgress(true));

    try {
      const response = await filesService.hasDocuments(String(companyId), String(measurementDateId));
      return response;
    } catch (error) {
      processErrorResponse({
        error,
        config: {
          [ERROR_403]: {
            notification: false,
          },
        },
        action: 'has documents',
      });
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
  };

  const getDocumentById = async (documentId: string | number): Promise<DocumentReferencesView> => {
    try {
      const response = await filesService.getDocumentById(String(documentId));
      return response as DocumentReferencesView;
    } catch (error) {
      Logger.error('Error getting document by id', error);
      throw error;
    }
  };

  const convertPdfTablesToXlsx = async (
    fileId: string | number,
    companyId: string | number,
    pageNumbers: number[]
  ): Promise<any> => {
    try {
      setIsLoading(true);
      const response = await filesService.convertPdfTablesToXlsx(String(fileId), String(companyId), {
        page_numbers: pageNumbers,
      });

      successNotification('Document generation job created successfully');

      return response;
    } catch (error) {
      Logger.error(`Error converting PDF tables to Excel for file ${fileId}`, error);
      errorNotification('Failed to create table extraction job');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const delegateDocumentRequest = async (
    requestId: string,
    token: string,
    data: DelegateDocumentRequest
  ) => {
    try {
      setIsLoading(true);
      const response = await DocumentsService.apiDocumentsRequestsDelegateCreate(requestId, token, data);
      successNotification('Request delegated successfully');
      return response;
    } catch (error) {
      Logger.error('Error delegating document request', error);
      errorNotification('Failed to delegate request');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const redelegateDocumentRequest = async (
    requestId: string,
    token: string,
    data: RedelegateDocumentRequest
  ) => {
    try {
      setIsLoading(true);
      const response = await DocumentsService.apiDocumentsRequestsRedelegateCreate(requestId, token, data);
      successNotification('Request re-delegated successfully');
      return response;
    } catch (error) {
      Logger.error('Error re-delegating document request', error);
      errorNotification('Failed to re-delegate request');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    getPreSignedUrl,
    createFileRepresentation,
    getDocuments,
    uploadFile,
    deleteFile,
    deleteMultipleFiles,
    updateFile,
    downloadDocument,
    downloadValuationReport,
    downloadMultipleDocuments,
    getReferencesByMeasurementDateId,
    isLoading,
    setIsLoading,
    companyInfo,
    company_id,
    companyDocuments,
    setCompanyDocuments,
    getFeaturesObjects,
    featuresObjects,
    addDocumentReferencesObject,
    foundReferencedDocuments,
    addReferenceForExistingDocuments,
    createSubFolder,
    updateSubFolder,
    deleteFolder,
    addFilesToFolder,
    currentDocuments,
    setCurrentDocuments,
    filesToSave,
    setFilesToSave,
    validateAndUploadFile,
    progress,
    isValidType,
    addDocumentReference,
    sendDocumentRequest,
    validateDocumentRequestToken,
    generatePostSignedUrlViaHMACToken,
    updateRequestedFileUploadStatus,
    updateTaskStatusFromPortal,
    uploadRequestedFiles,
    isUploading,
    hasDocuments,
    getFeaturesObjectsById,
    getPortalLinks,
    getDocumentById,
    downloadDocumentContent,
    convertPdfTablesToXlsx,
    delegateDocumentRequest,
    redelegateDocumentRequest,
  };
};

export default useDocuments;
